import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";
import { ICredentials, ICredentialsInsurrance } from "./types/authorization";

import {
  IClientInsurrance,
  IDeviceUserInsurrance,
  IPolicyDeviceUserInsurrance,
  ICoverageDevicePlanUser,
  IDocumentsCoveragesInsurrance,
  IGlobalForm,
} from "./types/claims";

interface IProps {
  children: ReactNode;
}

interface IDataDocument {
  name: string;
  secondName: string;
  identificationNumber: string;
  identificationType: string;
  genderName: string;
  email: string;
  lineOne: string;
  addressHome: string;
  cityHome: string;
  countryClaim: string;
  cityClaim: string;
  lineOneClaim: string;
  messageTicket: string;
  deviceName: string;
  manufacterName: string;
  imeiOne: string;
  imeiSecond: string;
  dateClaim: string;
  hourClaim: string;
}

type IContext = {
  client: IClientInsurrance | null;
  devices: IDeviceUserInsurrance[];
  globalForm: IGlobalForm | null;
  plans: IPolicyDeviceUserInsurrance[];
  currentPlan: IPolicyDeviceUserInsurrance | undefined | null;
  coverages: ICoverageDevicePlanUser[];
  documents: IDocumentsCoveragesInsurrance[];
  headers: ICredentials;
  headersInsurrance: ICredentialsInsurrance;
  isLoadingSubmit: boolean;
  setDevices: Dispatch<SetStateAction<IDeviceUserInsurrance[]>>;
  setGlobalForm: Dispatch<SetStateAction<IGlobalForm | null | any>>;
  setClient: Dispatch<SetStateAction<IClientInsurrance | null>>;
  setPlans: Dispatch<SetStateAction<IPolicyDeviceUserInsurrance[]>>;
  setCurrentPlan: Dispatch<
    SetStateAction<IPolicyDeviceUserInsurrance | undefined | null>
  >;
  setCoverages: Dispatch<SetStateAction<ICoverageDevicePlanUser[]>>;
  setDocuments: Dispatch<SetStateAction<IDocumentsCoveragesInsurrance[]>>;
  setIsLoadingSubmit: Dispatch<SetStateAction<boolean>>;
  setHeaders: Dispatch<SetStateAction<ICredentials>>;
  setHeadersInsurrance: Dispatch<SetStateAction<ICredentialsInsurrance>>;
  clear: () => void;
  createClaimsFormat: (data: IDataDocument) => void;
};

export const ReclamationContext = React.createContext<IContext>({} as IContext);
export const useReclamationContext = () =>
  useContext<IContext>(ReclamationContext);

const ReclamationProvider = ({ children }: IProps) => {
  const [plans, setPlans] = useState<IPolicyDeviceUserInsurrance[]>([]);
  const [client, setClient] = useState<IClientInsurrance | null>(null);
  const [devices, setDevices] = useState<IDeviceUserInsurrance[]>([]);
  const [coverages, setCoverages] = useState<ICoverageDevicePlanUser[]>([]);
  const [documents, setDocuments] = useState<IDocumentsCoveragesInsurrance[]>(
    [],
  );
  const [globalForm, setGlobalForm] = useState<IGlobalForm | null>(null);
  const [headers, setHeaders] = useState<ICredentials>({
    refreshToken: "",
    accessToken: "",
  });
  const [headersInsurrance, setHeadersInsurrance] =
    useState<ICredentialsInsurrance>({
      token: "",
      type: "",
    });
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<
    IPolicyDeviceUserInsurrance | undefined | null
  >(null);

  const clear = () => {
    setPlans([]);
    setDevices([]);
    setCoverages([]);
    setDocuments([]);
    setGlobalForm(null);
  };

  const createClaimsFormat = (dataInfo: IDataDocument) => {
    const data = [
      "nombre: ",
      dataInfo.name,
      "\napellido: ",
      dataInfo.secondName,
      "\ndocumento: ",
      dataInfo.identificationNumber,
      "\ntipo_documento: ",
      dataInfo.identificationType,
      "\ngenero: ",
      dataInfo.genderName,
      "\nemail: ",
      dataInfo.email,
      "\nnumero_celular_asegurado: ",
      dataInfo.lineOne,
      "\ndireccion_residencia: ",
      dataInfo.addressHome,
      "\nciudad_residencia: ",
      dataInfo.cityHome,
      "\npais_siniestro: ",
      dataInfo.countryClaim,
      "\nciudad_siniestro: ",
      dataInfo.cityClaim,
      "\ncelular_siniestro: ",
      dataInfo.lineOneClaim,
      "\ndescripcion_siniestro: ",
      dataInfo.messageTicket,
      "\nmodelo_dispositivo: ",
      dataInfo.deviceName,
      "\nmarca_dispositivo: ",
      dataInfo.manufacterName,
      "\nimei_principal: ",
      dataInfo.imeiOne,
      "\nimei_secundario: ",
      dataInfo.imeiSecond,
      "\nfecha_siniestro: ",
      dataInfo.dateClaim.substring(0, 10),
      "\nhora_siniestro: ",
      new Date(dataInfo?.hourClaim).toLocaleTimeString(),
    ];
    const file = new File(data, "formato_reclamacion.txt", {
      type: "text/plain",
    });
    const idDoc = documents.find(
      (doc) => doc.nombre_documento_save === "Formato de reclamación.",
    );
    if (idDoc && globalForm?.documents) {
      globalForm.documents[idDoc.id] = [file];
    }
  };

  const context: IContext = useMemo(
    () => ({
      client,
      devices,
      globalForm,
      plans,
      coverages,
      documents,
      headers,
      headersInsurrance,
      isLoadingSubmit,
      currentPlan,
      setClient,
      setDevices,
      setGlobalForm,
      setPlans,
      setCoverages,
      setDocuments,
      setIsLoadingSubmit,
      setHeaders,
      setHeadersInsurrance,
      setCurrentPlan,
      clear,
      createClaimsFormat,
    }),
    [
      plans,
      client,
      devices,
      coverages,
      documents,
      isLoadingSubmit,
      headers,
      headersInsurrance,
      currentPlan,
      globalForm,
    ],
  );

  return (
    <ReclamationContext.Provider value={context}>
      {children}
    </ReclamationContext.Provider>
  );
};

export default ReclamationProvider;
